import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";
import { withVersion } from "@/lib/api/conflict";
import type { RegisterWrite } from "@/lib/api/gradebook";
export type OutcomeStatus =
  | "final_grade_not_submitted"
  | "proposal_pending_certification"
  | "certified"
  | "corrected";

export interface OutcomeProposal {
  id: string;
  result_letter: string;
  result_score: number | null;
  source_owner: "native_basic";
  source_reference: string;
  proposed_at: string;
}

export interface CertificationEvent {
  id: string;
  event_no: number;
  kind: "certification" | "correction";
  certified_letter: string;
  certified_score: number | null;
  prior_letter: string | null;
  prior_score: number | null;
  reason: string | null;
  downstream_impact: string | null;
  evidence_ref?: string;
  created_at: string;
}

export interface CourseOutcome {
  updated_at?: string;
  enrollment_id: string;
  display_name: string;
  status: OutcomeStatus;
  proposal: OutcomeProposal | null;
  standing_certification: CertificationEvent | null;
  certification_events: CertificationEvent[];
}

export interface CourseCloseoutSummary {
  grades_still_open: number;
  final_grades_submitted: number;
  proposals_awaiting_certification: number;
  fully_certified: number;
  corrections_present: number;
}

export interface CourseOutcomeProjection {
  timezone?: string;
  course_id: string;
  course_code: string;
  course_title: string;
  summary: CourseCloseoutSummary;
  outcomes: CourseOutcome[];
}

export interface StudentOutcome {
  course_id: string;
  course_code: string;
  course_title: string;
  enrollment_id: string;
  status: "provisional" | "awaiting_certification" | "certified" | "corrected";
  standing_certification: CertificationEvent | null;
  correction_history: CertificationEvent[];
}

export interface MyOutcomes {
  outcomes: StudentOutcome[];
}

export interface CertifyOutcomeBody {
  proposal_id: string;
  evidence_ref: string;
}

export interface ProposeOutcomeBody {
  result_letter: string;
  result_score: number | null;
  source_reference: string;
}

export interface CorrectOutcomeBody {
  certified_letter: string;
  certified_score: number | null;
  reason: string;
  downstream_impact: string;
  evidence_ref: string;
}

export const outcomesApi = {
  getCourse: (courseId: string) =>
    api.get<CourseOutcomeProjection>(`/courses/${encodeURIComponent(courseId)}/outcomes`),
  getMine: () => api.get<MyOutcomes>("/me/outcomes"),
  propose: (courseId: string, enrollmentId: string, body: ProposeOutcomeBody, write?: RegisterWrite) =>
    api.post<OutcomeProposal>(
      `/courses/${encodeURIComponent(courseId)}/enrollments/${encodeURIComponent(enrollmentId)}/proposal`,
      write ? withVersion(body, write.version) : body,
      write ? commitOptions(write.key) : {},
    ),
  certify: (enrollmentId: string, body: CertifyOutcomeBody, write?: RegisterWrite) =>
    api.post<CertificationEvent>(`/enrollments/${encodeURIComponent(enrollmentId)}/certification`, write ? withVersion(body, write.version) : body, write ? commitOptions(write.key) : {}),
  correct: (enrollmentId: string, body: CorrectOutcomeBody, write?: RegisterWrite) =>
    api.post<CertificationEvent>(`/enrollments/${encodeURIComponent(enrollmentId)}/correction`, write ? withVersion(body, write.version) : body, write ? commitOptions(write.key) : {}),
};
